import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField, Button, Box, Typography, Alert, IconButton, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails, Stack, CircularProgress,
  List, ListItem, ListItemAvatar, ListItemText, Avatar, Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AppCard from '../common/AppCard';
import StatusChip from '../common/StatusChip';
import ConfirmDialog from '../common/ConfirmDialog';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HomeIcon from '@mui/icons-material/Home';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../../services/api';
import { useData } from '../../contexts/DataContext';
import BankFeedManager from './BankFeedManager';

const actionBtnSx = { borderRadius: 3, textTransform: 'none', fontWeight: 600, boxShadow: 'none', px: 2.5 };

// Map Akahu account types to the same icons used on the Accounts page
const akahuAccountIcon = (type = '') => {
  const t = String(type).toUpperCase();
  if (t.includes('SAVINGS')) return <SavingsIcon />;
  if (t.includes('CREDIT')) return <CreditCardIcon />;
  if (t.includes('LOAN') || t.includes('MORTGAGE')) return <HomeIcon />;
  if (t.includes('INVESTMENT') || t.includes('KIWISAVER')) return <ShowChartIcon />;
  if (t.includes('WALLET')) return <AccountBalanceWalletIcon />;
  return <AccountBalanceIcon />;
};

// "2 hours ago" style label without extra dependencies
const timeAgo = (date) => {
  if (!date) return null;
  const mins = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export default function BankConnectionCard({ notify }) {
  const theme = useTheme();
  const { fetchTransactions, fetchAccounts } = useData();
  const [status, setStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // { inProgress, lastSync }
  const [bankAccounts, setBankAccounts] = useState(null); // null = not loaded
  const [accountsError, setAccountsError] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Legacy manual tokens
  const [akahuUserToken, setAkahuUserToken] = useState('');
  const [akahuAppToken, setAkahuAppToken] = useState('');
  const [showUserToken, setShowUserToken] = useState(false);
  const [showAppToken, setShowAppToken] = useState(false);
  const [savingTokens, setSavingTokens] = useState(false);
  // Balances are hidden by default and only revealed for this viewing session
  const [showBalances, setShowBalances] = useState(false);

  const loadBankAccounts = useCallback(() => {
    api.get('/akahu/accounts')
      .then(res => {
        setBankAccounts(res.data?.items || []);
        setAccountsError(false);
      })
      .catch(() => {
        setBankAccounts([]);
        setAccountsError(true);
      });
  }, []);

  const loadSyncStatus = useCallback(() => {
    api.get('/akahu/sync/status')
      .then(res => setSyncStatus(res.data))
      .catch(() => {});
  }, []);

  // While a sync is running, keep the status label live
  useEffect(() => {
    if (!syncStatus?.inProgress) return undefined;
    const t = setTimeout(loadSyncStatus, 5000);
    return () => clearTimeout(t);
  }, [syncStatus, loadSyncStatus]);

  useEffect(() => {
    api.get('/akahu/connection')
      .then(res => {
        setStatus(res.data);
        if (res.data?.connected) {
          loadBankAccounts();
          loadSyncStatus();
        }
      })
      .catch(() => setStatus(null));
    api.get('/settings')
      .then(res => {
        setAkahuUserToken(res.data.akahuUserToken || '');
        setAkahuAppToken(res.data.akahuAppToken || '');
      })
      .catch(() => {});
  }, [loadBankAccounts, loadSyncStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/akahu/connect', {
        params: { redirect_uri: `${window.location.origin}/auth/akahu/callback` }
      });
      window.location.href = res.data.url;
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to start the Akahu connection.', 'error');
      setConnecting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Ask Akahu to pull the latest data from the banks (best effort — the
      // sync below imports whatever Akahu already has either way, and any data
      // that lands later arrives automatically via webhooks).
      await api.post('/akahu/refresh').catch(() => {});
      const res = await api.post('/akahu/sync');
      if (res.status === 202 || res.data?.inProgress) {
        notify('A sync is already running. New transactions will appear shortly.');
      } else {
        const { inserted = 0, updated = 0 } = res.data || {};
        notify(`Synced: ${inserted} new, ${updated} updated.`);
      }
      await Promise.all([
        fetchTransactions({ silent: true }),
        fetchAccounts()
      ]);
      loadBankAccounts();
      loadSyncStatus();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to sync bank data.', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete('/akahu/connection');
      const res = await api.get('/akahu/connection');
      setStatus(res.data);
      setBankAccounts(null);
      notify('Your bank has been disconnected.');
      setDisconnectOpen(false);
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to disconnect Akahu.', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveTokens = async (e) => {
    e.preventDefault();
    setSavingTokens(true);
    try {
      await api.post('/settings', { akahuUserToken, akahuAppToken });
      notify('Manual tokens saved.');
    } catch (err) {
      notify('Failed to save tokens.', 'error');
    } finally {
      setSavingTokens(false);
    }
  };

  const connected = Boolean(status?.connected);
  const notConfigured = status?.configured === false;

  // Prefer the app's own last import time; fall back to Akahu's per-account
  // refresh timestamps for older setups.
  const lastSynced = syncStatus?.lastSync?.completedAt || (bankAccounts || [])
    .map(a => a.refreshed?.transactions || a.refreshed?.balance)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <AppCard
      title="Bank Connection"
      icon={<AccountBalanceIcon />}
      action={
        <StatusChip
          status={connected ? 'success' : notConfigured ? 'warning' : 'neutral'}
          label={connected ? 'Connected' : notConfigured ? 'Not set up' : 'Not connected'}
        />
      }
    >
      {connected ? (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Box>
              <Typography variant="body1" fontWeight={600}>Connected via Akahu</Typography>
              <Typography variant="body2" color="text.secondary">
                {status.connectedAt && `Linked on ${new Date(status.connectedAt).toLocaleDateString('en-NZ', {
                  day: 'numeric', month: 'long', year: 'numeric'
                })}`}
                {syncStatus?.inProgress ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: status.connectedAt ? 1.5 : 0 }}>
                    <CircularProgress size={12} sx={{ mr: 0.25 }} />
                    Syncing…
                  </Box>
                ) : lastSynced && (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: status.connectedAt ? 1.5 : 0 }}>
                    <ScheduleIcon sx={{ fontSize: '0.95rem', verticalAlign: 'text-bottom' }} />
                    Last synced {timeAgo(lastSynced)}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>

          {/* Linked accounts */}
          {bankAccounts === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : accountsError ? (
            <Typography variant="body2" color="text.secondary">
              Couldn't load your linked accounts right now.
            </Typography>
          ) : bankAccounts.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Button
                  size="small"
                  startIcon={showBalances ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  onClick={() => setShowBalances(s => !s)}
                  sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                >
                  {showBalances ? 'Hide balances' : 'Show balances'}
                </Button>
              </Box>
              <List
                dense
                disablePadding
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
              >
                {bankAccounts.map((acc, i) => (
                  <React.Fragment key={acc._id || i}>
                    {i > 0 && <Divider component="li" />}
                    <ListItem sx={{ py: 1 }}>
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 32, height: 32, borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            '& svg': { fontSize: '1.1rem' }
                          }}
                        >
                          {akahuAccountIcon(acc.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={acc.name}
                        secondary={acc.connection?.name || acc.formatted_account || null}
                        primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                        {showBalances ? (acc.formattedBalance?.currentFormatted || '') : '••••••'}
                      </Typography>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary">
            New transactions import automatically when your bank reports activity.
            Your bank feed is read-only: Budget Clues can import transactions and balances,
            but can never move money.
          </Typography>
          <BankFeedManager notify={notify} />
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button variant="outlined" onClick={handleConnect} disabled={connecting || notConfigured} sx={actionBtnSx}>
              {connecting ? 'Redirecting…' : 'Manage bank accounts'}
            </Button>
            <Button
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={actionBtnSx}
            >
              {refreshing ? 'Syncing…' : 'Refresh & sync'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => setDisconnectOpen(true)}
              sx={actionBtnSx}
            >
              Disconnect all banks
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Securely connect your bank through Akahu. You'll be taken to Akahu to log in and
            choose which accounts to share. Budget Clues only ever gets read-only access.
          </Typography>
          {notConfigured && (
            <Alert severity="info">
              Akahu isn't configured on the server yet. You can use the manual tokens below in the meantime.
            </Alert>
          )}
          <Button
            variant="contained"
            startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <AccountBalanceIcon />}
            onClick={handleConnect}
            disabled={connecting || notConfigured}
            sx={{ ...actionBtnSx, alignSelf: 'flex-start' }}
          >
            {connecting ? 'Redirecting…' : 'Connect your bank'}
          </Button>
        </Stack>
      )}

      {/* Advanced: legacy manual tokens, de-emphasised in an expander */}
      <Accordion
        disableGutters
        elevation={0}
        sx={{ mt: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>Advanced: manual tokens (legacy)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            For the older personal-app setup. These grant access to your live bank feed,
            so treat them like passwords.
          </Typography>
          <Box component="form" onSubmit={handleSaveTokens} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Akahu User Token"
              type={showUserToken ? 'text' : 'password'}
              value={akahuUserToken}
              onChange={e => setAkahuUserToken(e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showUserToken ? 'Hide user token' : 'Show user token'}
                      onClick={() => setShowUserToken(s => !s)}
                      edge="end"
                      size="small"
                    >
                      {showUserToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Akahu App Token"
              type={showAppToken ? 'text' : 'password'}
              value={akahuAppToken}
              onChange={e => setAkahuAppToken(e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showAppToken ? 'Hide app token' : 'Show app token'}
                      onClick={() => setShowAppToken(s => !s)}
                      edge="end"
                      size="small"
                    >
                      {showAppToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={savingTokens}
              sx={{ ...actionBtnSx, alignSelf: 'flex-start' }}
            >
              {savingTokens ? 'Saving…' : 'Save tokens'}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title="Disconnect all banks?"
        message="This revokes Budget Clues' access to your bank via Akahu and frees up a connection slot. You can reconnect at any time."
        confirmLabel="Disconnect"
        onConfirm={handleDisconnect}
        loading={disconnecting}
      />
    </AppCard>
  );
}

